import {useContext, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {AuthContext} from '@/context/Auth/AuthContext'
import {isFeatureEnabled} from '@/helpers/featureToggle'
import {FeatureToggle} from '@/utils/constants/featuresFlag'
import {initialState, UserRoleContext} from './UserRoleContext'
import {useCountries} from '@/hooks/useCountries'
import {UserRoleEnumSchema} from '@/types/UserRole'
import {getUserRoleUseCase} from '@/useCases/getUserRoleUseCase'

export const UserRoleProvider = ({children}) => {
  const {isAuthenticated, authState} = useContext(AuthContext)
  const {countries} = useCountries()

  const {data, error} = useQuery({
    queryKey: [`userRole-${authState?.accessToken?.claims?.gpid ?? ''}`],
    queryFn: ({signal}) => getUserRoleUseCase(signal),
    enabled: isFeatureEnabled(FeatureToggle.userRole) && isAuthenticated,
  })

  const value = useMemo(() => {
    if (!isFeatureEnabled(FeatureToggle.userRole)) {
      return {
        ...initialState,
        isReady: true,
        isAuthorized: true,
        userRole: UserRoleEnumSchema.Values.admin,
      }
    }

    if (!data || error) {
      return {
        ...initialState,
        error,
      }
    }

    const userRole = data[0]?.role
    const userAccessRules = accessRuleToCountryCodeAdapter(data[0]?.accessRules?.[0]?.values ?? [])
    const isAdmin = userRole === UserRoleEnumSchema.Values.admin
    const isAuthorized = isAdmin ? true : countries.some(country => userAccessRules.includes(country.code))

    return {
      ...initialState,
      isReady: true,
      isAuthorized,
      userRole,
      userAccessRules,
    }
  }, [countries, data, error])

  return <UserRoleContext.Provider value={value}>{children}</UserRoleContext.Provider>
}

/**
 * Parse market from accessRules pattern adapting it to ["countryCode"] format.
 * Accepted patterns:
 *  "region-market" -> ["Europe-TR"]
 *  "market" ->  ["TR"]
 * @param accessRule array of accessRules strings
 * @returns array of countrCodes user has access to: ["TR"]
 */
const accessRuleToCountryCodeAdapter = (accessRule = []) => {
  return accessRule.map(rule => {
    const [, market] = rule?.split('-') ?? []
    return market ?? rule
  })
}
